/* The per-user share-sheet webhook.

   The URL embeds a token that lets anyone holding it write into this user's
   library, so it is masked by default and revealed deliberately — v1 rendered it
   in plaintext, which is awkward on a shared screen. Copy works without
   revealing, because copying is the common case and reading it is not.

   The actions live inside the field's right edge, acting on the value they sit
   in. The sweep's 4px-gap finding is about archive/delete, where a mis-tap is
   destructive and unrecoverable; reveal and copy are both reversible and one is
   idempotent, so the fix was air (8px on a coarse pointer) rather than the split
   bar this briefly became — see ADR 0011 decision 4.

   The instruction above it and the warning below it are rendered by g-settings:
   prose belongs to the group, controls belong to the component.

   There is no rotate action: reissuing the token needs a backend endpoint that
   doesn't exist yet. */

import { LitElement, html, css } from 'lit';
import { iconActions, shadowReset } from '../../design/base.js';
import { settingsRow } from './row.js';
import { customElement, property, state } from 'lit/decorators.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';

@customElement('settings-webhook')
export class SettingsWebhook extends LitElement {
  static override styles = [
    shadowReset,
    settingsRow,
    iconActions,
    css`
      :host {
        display: block;
      }
      /* An outlined, unfilled object inset to the row's text edge — the same
         shape the theme segmented bar takes, for the same reason: on a flat page
         a control is an outline, and a fill would be a third tone. */
      .url {
        display: flex;
        /* center, not start: the buttons are taller than one line of text, so
           top-aligning them drops their icons below the text's optical centre */
        align-items: center;
        gap: var(--space-1);
        margin: var(--space-2) var(--row-pad-r) var(--space-2)
          var(--row-pad-l);
        padding: var(--space-1) var(--space-1) var(--space-1) var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        font-family: var(--font-mono);
        font-size: 0.75rem;
      }
      /* Revealed: wrap so the whole token is actually readable — that's the
         entire point of revealing it. min-width:0 is what lets the masked
         ellipsis engage inside the flex row. */
      .value {
        flex: 1;
        min-width: 0;
        word-break: break-all;
      }
      /* Masked: one compact line. Nothing is lost by truncating bullets, and it
         keeps the block short in the state it's in 99% of the time. */
      .masked {
        color: var(--text-dim);
        letter-spacing: 0.08em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* Visually these read as bare icons; they stay real buttons so they keep
         keyboard focus, a focus ring and an accessible name. Appearance comes
         from iconActions — no revealOnHover here, since a control you have to
         discover by hovering would hide the only way to read your own webhook. */
      @media (pointer: coarse) {
        .acts {
          gap: var(--space-2);
        }
      }
    `,
  ];

  @property() url = '';
  @state() private revealed = false;
  @state() private copied = false;

  /** Keep the origin readable; hide only the token segment. */
  private masked(): string {
    const i = this.url.lastIndexOf('/');
    return i < 0 ? '••••••••' : `${this.url.slice(0, i + 1)}${'•'.repeat(12)}`;
  }

  private async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.url);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1600);
    } catch {
      // Clipboard can be blocked (permissions, insecure context). Revealing lets
      // the user select it by hand instead of silently failing.
      this.revealed = true;
    }
  }

  override render() {
    if (!this.url) return html`<g-notice class="text">Sign in to see your webhook URL.</g-notice>`;
    return html`
      <div class="url">
        <span
          class="value ${this.revealed ? '' : 'masked'}"
          title=${this.revealed ? this.url : 'Hidden — reveal to read it'}
          >${this.revealed ? this.url : this.masked()}</span
        >
        <div class="acts">
          <g-button
            icon-only
            label=${this.revealed ? 'Hide webhook URL' : 'Reveal webhook URL'}
            @click=${() => (this.revealed = !this.revealed)}
          >
            <g-icon name=${this.revealed ? 'eye-off' : 'eye'}></g-icon>
          </g-button>
          <g-button
            icon-only
            label=${this.copied ? 'Copied' : 'Copy webhook URL'}
            @click=${this.copy}
          >
            <g-icon name=${this.copied ? 'check' : 'copy'}></g-icon>
          </g-button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'settings-webhook': SettingsWebhook;
  }
}
